// src/pages/admin/Withdrawals.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import PaidIcon from "@mui/icons-material/Paid";
import RefreshIcon from "@mui/icons-material/Refresh";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

export default function WithdrawalsAdminPage() {
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: "" });
  const [confirmLoadingId, setConfirmLoadingId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const adminToken =
    typeof window !== "undefined"
      ? sessionStorage.getItem("admin_token") || localStorage.getItem("admin_token")
      : null;

  useEffect(() => {
    let mounted = true;

    async function doFetch() {
      if (!adminToken) {
        if (!mounted) return;
        setSnack({ open: true, severity: "error", message: "Admin token missing. Please login." });
        setWithdrawals([]);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${REACT_APP_API_URL}/rockstar/admin/withdrawals`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.message || `Failed to fetch withdrawals (${res.status})`);
        }
        const body = await res.json();
        const list = body?.data ?? body;
        if (!mounted) return;
        setWithdrawals(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("fetchWithdrawals err", err);
        if (!mounted) return;
        setSnack({ open: true, severity: "error", message: err.message || "Failed to load withdrawals" });
        setWithdrawals([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    doFetch();

    return () => {
      mounted = false;
    };
  }, [refreshKey, adminToken]);

  const handleApprove = async (id) => {
    if (!adminToken) {
      setSnack({ open: true, severity: "error", message: "Admin token missing" });
      return;
    }
    if (!window.confirm("Approve this withdrawal?")) return;

    setConfirmLoadingId(id);
    try {
      const res = await fetch(`${REACT_APP_API_URL}/rockstar/admin/withdrawals/${id}/approve`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to approve");
      setSnack({ open: true, severity: "success", message: body?.message || "Withdrawal approved" });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("approve err", err);
      setSnack({ open: true, severity: "error", message: err.message || "Failed to approve" });
    } finally {
      setConfirmLoadingId(null);
    }
  };

  const handleOpenReject = (id) => {
    setRejectDialog({ open: true, id, reason: "" });
  };

  const handleReject = async () => {
    const { id, reason } = rejectDialog;
    if (!id) return;
    if (!adminToken) {
      setSnack({ open: true, severity: "error", message: "Admin token missing" });
      return;
    }
    if (!reason.trim()) {
      setSnack({ open: true, severity: "error", message: "Please provide a reason for rejection" });
      return;
    }

    setConfirmLoadingId(id);
    try {
      const res = await fetch(`${REACT_APP_API_URL}/rockstar/admin/withdrawals/${id}/reject`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to reject");
      setSnack({ open: true, severity: "success", message: body?.message || "Withdrawal rejected" });
      setRejectDialog({ open: false, id: null, reason: "" });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("reject err", err);
      setSnack({ open: true, severity: "error", message: err.message || "Failed to reject" });
    } finally {
      setConfirmLoadingId(null);
    }
  };

  const handleMarkPaid = async (id) => {
    if (!adminToken) {
      setSnack({ open: true, severity: "error", message: "Admin token missing" });
      return;
    }
    if (!window.confirm("Mark this withdrawal as PAID?")) return;

    setConfirmLoadingId(id);
    try {
      const res = await fetch(`${REACT_APP_API_URL}/rockstar/admin/withdrawals/${id}/paid`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to mark as paid");
      setSnack({ open: true, severity: "success", message: body?.message || "Withdrawal marked as paid" });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("markPaid err", err);
      setSnack({ open: true, severity: "error", message: err.message || "Failed to mark paid" });
    } finally {
      setConfirmLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!adminToken) {
      setSnack({ open: true, severity: "error", message: "Admin token missing" });
      return;
    }
    if (!window.confirm("Permanently delete this withdrawal? This cannot be undone.")) return;

    setConfirmLoadingId(id);
    try {
      const res = await fetch(`${API_BASE}/rockstar/admin/withdrawals/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to delete");
      setSnack({ open: true, severity: "success", message: body?.message || "Deleted" });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("delete err", err);
      setSnack({ open: true, severity: "error", message: err.message || "Failed to delete" });
    } finally {
      setConfirmLoadingId(null);
    }
  };

  const statusColor = (status) => {
    switch ((status || "").toUpperCase()) {
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "error";
      case "PENDING":
        return "warning";
      case "PROCESSED":
      case "PAID":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    // top padding so it won’t overlap a fixed header; centered content via inner maxWidth
    <Container
  maxWidth="lg"
  sx={{
    pt: { xs: 10, sm: 12 }, // adds enough space under header
    pb: 6,
    display: "flex",
    justifyContent: "center",
  }}
>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" fontWeight={700}>
              Withdrawal Requests
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => setRefreshKey((k) => k + 1)}
                size="small"
              >
                Refresh
              </Button>
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                size="small"
                onClick={() => {
                  if (window.confirm("Delete all rejected withdrawals?")) {
                    setSnack({ open: true, severity: "info", message: "Bulk delete not implemented" });
                  }
                }}
              >
                Bulk
              </Button>
            </Stack>
          </Box>

          {loading && <LinearProgress />}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Rockstar ID</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Profit</TableCell>
                <TableCell>Deposit</TableCell>
                <TableCell>Requested At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Processed By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {withdrawals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No withdrawal requests found.
                  </TableCell>
                </TableRow>
              ) : (
                withdrawals.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>{w.id}</TableCell>
                    <TableCell>
                      {w.user?.name ? `${w.user.name}` : (w.user?.email || "-")}
                      <br />
                      <Typography variant="caption" color="text.secondary">{w.user?.email || ""}</Typography>
                    </TableCell>
                    <TableCell>{w.rockstarId || "-"}</TableCell>
                    <TableCell>{Number(w.amount).toFixed(2)}</TableCell>
                    <TableCell>{w.profitId ?? "-"}</TableCell>
                    <TableCell>{w.depositId ?? "-"}</TableCell>
                    <TableCell>{w.requestedAt ? new Date(w.requestedAt).toLocaleString() : "-"}</TableCell>
                    <TableCell>
                      <Chip label={(w.status || "").toUpperCase()} color={statusColor(w.status)} size="small" />
                      {w.reason ? <Typography variant="caption" display="block">{w.reason}</Typography> : null}
                    </TableCell>
                    <TableCell>{w.processedBy ?? "-"}</TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Approve">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleApprove(w.id)}
                              disabled={confirmLoadingId === w.id || (w.status || "").toUpperCase() !== "PENDING"}
                            >
                              <CheckIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Reject">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenReject(w.id)}
                              disabled={confirmLoadingId === w.id || (w.status || "").toUpperCase() !== "PENDING"}
                            >
                              <CloseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Mark as Paid">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMarkPaid(w.id)}
                              disabled={confirmLoadingId === w.id || (w.status || "").toUpperCase() !== "APPROVED"}
                            >
                              <PaidIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(w.id)}
                              disabled={confirmLoadingId === w.id}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: null, reason: "" })} fullWidth maxWidth="sm">
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this withdrawal (this will be sent to the user).
          </Typography>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog((s) => ({ ...s, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, id: null, reason: "" })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleReject} disabled={confirmLoadingId === rejectDialog.id}>
            {confirmLoadingId === rejectDialog.id ? "Processing..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
